import { useRef, useState } from 'react';

const DEFAULT_PADDING = 0;
const DEFAULT_MIN_HEIGHT = 240;
const DEFAULT_MAX_HEIGHT = 3840;
const DEFAULT_ITEM_HEIGHT = 16;

const isScrollable = (element: HTMLElement): boolean => {
  const styles = window.getComputedStyle(element);
  const { overflow, overflowY, overflowX } = styles;

  return /(auto|scroll)/.test(overflow + overflowY + overflowX);
};

const getScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let node: HTMLElement | null = element;

  while (node && node !== document.body && node !== document.documentElement) {
    if (isScrollable(node)) return node;

    node = node.parentElement;
  }
  return document.scrollingElement as HTMLElement;
};

const getAvailableHeight = (
  anchorElement: HTMLElement,
  padding: number,
  minHeight: number,
  maxHeight: number,
): number => {
  const scrollableElement = getScrollableAncestor(anchorElement) || document.documentElement;
  const anchorRect = anchorElement.getBoundingClientRect();
  const scrollableRect = scrollableElement.getBoundingClientRect();
  const availableSpace =
    (scrollableElement === document.documentElement ? window.innerHeight : scrollableRect.bottom) -
    anchorRect.top -
    padding;

  return Math.max(minHeight, Math.min(maxHeight, Math.floor(availableSpace)));
};

interface UseAvailableHeightReturn {
  availableExpandedHeights: Record<string, number>;
  setHeight: (name: string, itemCount: number) => void;
  getHeight: (name: string) => number | undefined;
  setRefElement: (name: string, element: HTMLDivElement | null) => void;
}

interface UseAvailableHeightOptions {
  padding?: number;
  minHeight?: number;
  maxHeight?: number;
  itemHeight?: number;
}

export const useAvailableHeight = (options: UseAvailableHeightOptions = {}): UseAvailableHeightReturn => {
  const {
    padding = DEFAULT_PADDING,
    minHeight = DEFAULT_MIN_HEIGHT,
    maxHeight = DEFAULT_MAX_HEIGHT,
    itemHeight = DEFAULT_ITEM_HEIGHT,
  } = options;

  const [availableExpandedHeights, setAvailableExpandedHeights] = useState<Record<string, number>>({});
  const refElements = useRef<Record<string, HTMLDivElement | null>>({});

  const calculateHeight = (name: string, itemCount: number): number => {
    const anchorElement = refElements.current[name];

    if (!!minHeight && minHeight > itemCount * itemHeight) {
      return minHeight;
    }

    if (anchorElement) {
      const availableHeight = getAvailableHeight(anchorElement, padding, minHeight, maxHeight);

      return Math.min(availableHeight, itemCount * itemHeight);
    }

    return minHeight;
  };

  const setHeight = (name: string, itemCount: number): void => {
    const height = calculateHeight(name, itemCount);

    setAvailableExpandedHeights((prev) => ({ ...prev, [name]: height }));
  };

  const getHeight = (name: string): number | undefined => {
    return availableExpandedHeights[name];
  };

  const setRefElement = (name: string, element: HTMLDivElement | null): void => {
    refElements.current[name] = element;
  };

  return {
    availableExpandedHeights,
    setHeight,
    getHeight,
    setRefElement,
  };
};
